/// <amd-module name="Wit/LinkedWorkItemsDropdown/Components/LinkedWorkItemsDropdown" />
import * as React from "react";
import { getTFSProject, PartialProjectData } from "Tfs/Platform/Page";
import { IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { Components, css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { FormItemContext } from "VSSUI/FormItem";
import { Link } from "VSSUI/Link";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { IMessageCardProps, MessageCard } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Pill, PillSize } from "VSSUI/Pill";
import { ITableRow } from "VSSUI/Table";
import { getSafeId } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import * as Resources from "Wit/LinkedWorkItemsDropdown/Resources";
import { IArtifactLink, ILinkedWorkItemsService } from "../LinkedWorkItemsService";
import { IWorkItemEntry, workItemToListBoxItem } from "../Util/Data";
import { ControlledLinkedWorkItemsDropdown } from "./ControlledLinkedWorkItemsDropdown";
import { LinkedWorkItemsTable } from "./LinkedWorkItemsTable";

export interface IArtifactChangesContext {
    addArtifactCompleteListener: (listener: ArtifactCompleteListener) => void;
}

export declare type ArtifactCompleteListener = (artifactLinks: IArtifactLink[]) => void | Promise<void>;

export interface ILinkedWorkItemsDropdownProps {
    artifactChangesContext: IArtifactChangesContext;
    initialWorkItems?: Promise<number[]> | number[];
    message?: IReadonlyObservableValue<(IMessageCardProps & { children: React.ReactNode }) | undefined>;
    onSelectedWorkItemsChanged?: (workItemIds: WorkItem[]) => void;
    /**
     * If true, the dropdown will show an additional search button at the bottom.
     * After this button is pressed, we will search for additional results to
     * include in the suggestions as the user types.
     */
    withLiveTextSearch?: boolean;
    /**
     * A filter function that will restrict which work items can appear as
     * suggestions and be selected.
     */
    workItemSuggestionFilter?: (workItem: WorkItem) => boolean;
    workItemProjectData?: PartialProjectData;
}

interface ILinkedWorkItemsDropdownState {
    searchOnType: boolean;
}

const searchDebounceMs = 300;

export class LinkedWorkItemsDropdown extends VssComponent<ILinkedWorkItemsDropdownProps, ILinkedWorkItemsDropdownState> {
    private hasIdBasedItem: boolean = false;
    private initialItemsLoading = new ObservableValue(false);
    private project: PartialProjectData;
    private selection = new DropdownSelection();
    private workItemSuggestions = new ObservableArray<IListBoxItem<WorkItem>>();
    private previousSuggestions: Array<IListBoxItem<WorkItem>> = new Array();

    private selectedWorkItems: ObservableArray<IListBoxItem<WorkItem>> = new ObservableArray();
    private selectedWorkItemIds: Set<string> = new Set<string>();
    private text = new ObservableValue<string>("");

    private linkedWorkItemsService: ILinkedWorkItemsService;

    private disableSuggestions: boolean = false;
    private useSearchApi: boolean;

    private debouncedSearchById = debounce(
        (id: number, suggestions: ObservableArray<IListBoxItem<WorkItem>>) =>
            isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.DisableRelatedWitLoadingIndicator, false)
                ? this.searchById(id)
                : this.loadingPlaceholderWrapper(this.filterSuggestionsById(id, [...suggestions.value])),
        searchDebounceMs
    );

    private debouncedSearchByTitle = debounce(
        (text: string, suggestions: ObservableArray<IListBoxItem<WorkItem>>) =>
            isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.DisableRelatedWitLoadingIndicator, false)
                ? this.searchByTitle(text)
                : this.loadingPlaceholderWrapper(this.filterSuggestionsByTitle(text, [...suggestions.value])),
        searchDebounceMs
    );

    constructor(props: ILinkedWorkItemsDropdownProps, context: IVssComponentContext) {
        super(props, context);
        this.state = { searchOnType: false };

        //Handles the case when we are doing an organization level search. We need to set workitem's projectId.
        this.project = (getTFSProject(this.context.pageContext) as PartialProjectData) ?? this.props?.workItemProjectData;

        if (!this.project) {
            throw new Error(Resources.NoProjectSpecifiedMessage);
        }

        this.linkedWorkItemsService = this.context.pageContext.getService<ILinkedWorkItemsService>("ILinkedWorkItemsService");
        this.disableSuggestions = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.DisableSuggestions, false);
        this.useSearchApi = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.SearchAPIForWorkItemLinksEnabled, false);
        this.setInitialWorkItems(props.initialWorkItems ?? []);
        this.initializeSuggestions();
        props.artifactChangesContext.addArtifactCompleteListener(this.linkWorkItems);
    }

    public render() {
        return (
            <Observer
                initialItemsLoading={this.initialItemsLoading}
                message={this.props.message}
                selectedWorkItems={this.selectedWorkItems}
                workItems={this.workItemSuggestions}
            >
                {(props: { initialItemsLoading: boolean; message?: IMessageCardProps }) => {
                    return (
                        <div className="flex-column rhythm-vertical-8">
                            <FormItemContext.Provider value={{ ariaLabelledById: "pr-linked-workitems-label" }}>
                                <div className="flex-row flex-center rhythm-horizontal-8">
                                    <label className="body-m" id={getSafeId("pr-linked-workitems-label")}>
                                        {Resources.WorkItemsToLinkLabel}
                                    </label>
                                    {(this.selectedWorkItems.value.length > 0 || props.initialItemsLoading) && (
                                        <Pill excludeFocusZone={true} size={PillSize.compact} tabIndex={-1}>
                                            {props.initialItemsLoading ? Resources.Loading : this.selectedWorkItems.value.length}
                                        </Pill>
                                    )}
                                    <span className="flex-grow" />
                                    <Link
                                        className={css(!this.selectedWorkItems.value.length && "invisible")}
                                        onClick={() => {
                                            this.selectedWorkItems.value = [];
                                            this.selectedWorkItemIds.clear();
                                            this.triggerWorkItemsChanged();
                                        }}
                                        subtle={true}
                                        tooltipProps={{ text: Resources.ClearAllTooltip }}
                                    >
                                        {Resources.ClearAll}
                                    </Link>
                                </div>
                                <ControlledLinkedWorkItemsDropdown
                                    excludedSearchIds={this.selectedWorkItemIds}
                                    onTextChanged={this.onTextChange}
                                    onValueChanged={this.addLinkedWorkItem}
                                    selection={this.selection}
                                    text={this.text}
                                    workItems={this.workItemSuggestions}
                                    onSearchClick={this.state.searchOnType || !this.props.withLiveTextSearch ? undefined : this.onSearchClick}
                                />
                            </FormItemContext.Provider>
                            {props.message && <MessageCard {...props.message} />}
                            <LinkedWorkItemsTable
                                expanded={false}
                                itemProvider={this.selectedWorkItems}
                                onActivate={(event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<IWorkItemEntry>) => {
                                    if (!event.defaultPrevented) {
                                        onClickFPS(this.context.pageContext, tableRow.data.href!, true, event);
                                    }
                                }}
                                onRemoveItem={this.removeWorkItem}
                            />
                        </div>
                    );
                }}
            </Observer>
        );
    }

    public removeWorkItems(workItemIdsToRemove: number[]): void {
        this.selectedWorkItems.removeAll(selectedItem => {
            return workItemIdsToRemove.indexOf(selectedItem.data!.id) >= 0;
        });

        workItemIdsToRemove.forEach(x => this.selectedWorkItemIds.delete(x.toString()));

        this.triggerWorkItemsChanged();
    }

    private loadingPlaceholderWrapper(filterFunction: Promise<IListBoxItem<WorkItem>[] | undefined>): void {
        const loadingRowId = "loading-row";
        if (!this.workItemSuggestions.value.some(suggestion => suggestion.id === loadingRowId)) {
            this.previousSuggestions = [...this.workItemSuggestions.value];
        }
        this.workItemSuggestions.value = [{ id: loadingRowId, type: ListBoxItemType.Loading, text: loadingRowId }];
        filterFunction
            .then(vals => {
                if (vals) {
                    this.workItemSuggestions.value = vals;
                } else {
                    this.workItemSuggestions.value = this.previousSuggestions;
                }
            })
            .catch(() => {
                this.workItemSuggestions.value = this.previousSuggestions;
            });
    }

    private async setInitialWorkItems(initialWorkItems: Promise<number[]> | number[]) {
        const initialWorkItemIds = await initialWorkItems;
        if (initialWorkItemIds.length === 0) {
            return;
        }
        const workItems = await this.linkedWorkItemsService.getWorkItems(initialWorkItemIds, this.project.name);
        for (const workItem of workItems) {
            const listBoxItem = workItemToListBoxItem(this.context.pageContext, workItem);
            this.addLinkedWorkItem(listBoxItem);
        }
        this.initialItemsLoading.value = false;
    }

    private async initializeSuggestions() {
        this.workItemSuggestions.push({ id: "loading-row", type: ListBoxItemType.Loading, text: "loading-row" });

        try {
            const { workItems } = this.disableSuggestions
                ? { workItems: [] }
                : await this.linkedWorkItemsService.getAssignedWorkItems(this.project.id);
            if (workItems.length === 0) {
                this.workItemSuggestions.removeAll();
                return;
            }
            const assignedWorkItemIds = workItems.map(wi => wi.id);
            const assignedWorkItems = await this.linkedWorkItemsService.getWorkItems(assignedWorkItemIds, this.project.name);

            const newSuggestionItems: IListBoxItem<WorkItem>[] = [];
            for (const workItem of assignedWorkItems.filter(this.workItemSuggestionFilter)) {
                const listBoxItem: IListBoxItem<WorkItem> = workItemToListBoxItem(this.context.pageContext, workItem);
                newSuggestionItems.push(listBoxItem);
            }

            this.workItemSuggestions.value = this.previousSuggestions = newSuggestionItems;
        } catch (e: unknown) {
            this.workItemSuggestions.removeAll();
        }
    }

    private onSearchClick = (): void => {
        this.setState({ searchOnType: true });
        isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.DisableRelatedWitLoadingIndicator, false)
            ? this.searchByTitle(this.text.value)
            : this.loadingPlaceholderWrapper(this.filterSuggestionsByTitle(this.text.value, [...this.workItemSuggestions.value]));
    };

    private triggerWorkItemsChanged(): void {
        if (this.props.onSelectedWorkItemsChanged) {
            this.props.onSelectedWorkItemsChanged(this.selectedWorkItems.value.map(l => l.data!));
        }
    }

    private linkWorkItems = async (artifactLinks: IArtifactLink[]): Promise<void> => {
        if (!this.selectedWorkItems.length) {
            return;
        }

        const linkedWorkItemIds = this.selectedWorkItems.value.map(item => parseInt(item.id));
        return this.linkedWorkItemsService.addWorkItemsBatchAsync(linkedWorkItemIds, artifactLinks);
    };

    private onTextChange = (ev: any, text: string) => {
        this.text.value = text;
        const alreadyInList = this.workItemSuggestions.value.some(item => {
            return text === item.id;
        });
        const id = parseInt(text);
        if (!isNaN(id) && !alreadyInList) {
            this.debouncedSearchById(id, this.workItemSuggestions);
        } else if (this.state.searchOnType && this.props.withLiveTextSearch) {
            this.debouncedSearchByTitle(text, this.workItemSuggestions);
        } else {
            this.hasIdBasedItem && this.workItemSuggestions.splice(0, 1);
            this.hasIdBasedItem = false;
        }
    };

    private async filterSuggestionsById(id: number, suggestions: IListBoxItem<WorkItem>[]): Promise<IListBoxItem<WorkItem>[] | undefined> {
        try {
            const workItem = await this.linkedWorkItemsService.getWorkItem(id, this.project.name);
            if (id !== parseInt(this.text.value)) {
                return; // The input has changed; do nothing with this response
            }

            if (!this.workItemSuggestionFilter(workItem)) {
                // We should not show this match
                return;
            }
            const listBoxItem = workItemToListBoxItem(this.context.pageContext, workItem);
            suggestions.splice(0, this.hasIdBasedItem ? 1 : 0, listBoxItem);
            this.hasIdBasedItem = true;
            return suggestions;
        } catch (e) {
            this.hasIdBasedItem && suggestions.splice(0, 1);
            this.hasIdBasedItem = false;
            return;
        }
    }

    private async filterSuggestionsByTitle(text: string, suggestions: IListBoxItem<WorkItem>[]): Promise<IListBoxItem<WorkItem>[] | undefined> {
        if (text.trim().length === 0) {
            return;
        }

        const idsToExclude = suggestions.map(wi => parseInt(wi.id)).filter(wid => !isNaN(wid));

        const workItemIds = await this.linkedWorkItemsService.searchWorkItemIds(text, idsToExclude, this.project.id, this.useSearchApi);

        if (!workItemIds?.length) {
            return suggestions;
        }

        if (text !== this.text.value) {
            return; // The input has changed; do nothing with this response
        }

        const workItems = await this.linkedWorkItemsService.getWorkItems(workItemIds, this.project.name);
        if (text !== this.text.value) {
            return; // The input has changed; do nothing with this response
        }

        const existingIds = suggestions.map(wi => parseInt(wi.id));
        const newItems = workItems
            .filter(this.workItemSuggestionFilter)
            .filter(wi => !existingIds.includes(wi.id))
            .map(wi => workItemToListBoxItem(this.context.pageContext, wi));

        suggestions.splice(0, 0, ...newItems);
        return suggestions;
    }

    private async searchById(id: number): Promise<void> {
        try {
            const workItem = await this.linkedWorkItemsService.getWorkItem(id, this.project.name);
            if (id !== parseInt(this.text.value)) {
                return; // The input has changed; do nothing with this response
            }

            if (!this.workItemSuggestionFilter(workItem)) {
                // We should not show this match
                return;
            }
            const listBoxItem = workItemToListBoxItem(this.context.pageContext, workItem);
            this.workItemSuggestions.splice(0, this.hasIdBasedItem ? 1 : 0, listBoxItem);
            this.hasIdBasedItem = true;
        } catch (e) {
            this.hasIdBasedItem && this.workItemSuggestions.splice(0, 1);
            this.hasIdBasedItem = false;
        }
    }

    private async searchByTitle(text: string): Promise<void> {
        if (text.trim().length === 0) {
            return;
        }

        const idsToExclude = this.workItemSuggestions.value.map(wi => parseInt(wi.id)).filter(wid => !isNaN(wid));
        const workItemIds = await this.linkedWorkItemsService.searchWorkItemIds(text, idsToExclude, this.project.id);

        if (!workItemIds?.length) {
            return;
        }

        if (text !== this.text.value) {
            return; // The input has changed; do nothing with this response
        }

        const workItems = await this.linkedWorkItemsService.getWorkItems(
            workItemIds,
            this.project.name
        );
        if (text !== this.text.value) {
            return; // The input has changed; do nothing with this response
        }

        const existingIds = this.workItemSuggestions.value.map(wi => parseInt(wi.id));
        const newItems = workItems
            .filter(this.workItemSuggestionFilter)
            .filter(wi => !existingIds.includes(wi.id))
            .map(wi => workItemToListBoxItem(this.context.pageContext, wi));

        this.workItemSuggestions.splice(0, 0, ...newItems);
    }

    private addLinkedWorkItem = (value: IListBoxItem<WorkItem>) => {
        if (value) {
            this.selection.value = [];
            this.selectedWorkItems.push(value);
            this.selectedWorkItemIds.add(value.id);
            this.triggerWorkItemsChanged();
        }
    };

    private removeWorkItem = (workItem: IListBoxItem<WorkItem>) => {
        this.selectedWorkItems.removeAll(selectedItem => {
            return selectedItem.id === workItem.id;
        });
        this.selectedWorkItemIds.delete(workItem.id);
        this.triggerWorkItemsChanged();
    };

    private workItemSuggestionFilter = (workItem: WorkItem) => {
        return this.props.workItemSuggestionFilter ? this.props.workItemSuggestionFilter(workItem) : true;
    };
}

function debounce<T extends any[]>(functionToDebounce: (...args: T) => void, debounceMs: number): (...args: T) => void {
    let timeout: number | null = null;

    return (...args: T) => {
        if (timeout !== null) {
            clearTimeout(timeout);
        }

        timeout = setTimeout(() => {
            timeout = null;
            functionToDebounce(...args);
        }, debounceMs);
    };
}

Components.add("WIT.LinkedWorkItemsDropdown", LinkedWorkItemsDropdown);
